import { z } from 'zod';

export const domainEventTypes = ['LeadCreated', 'LeadAssigned', 'LeadQualified', 'LeadProposed', 'DealWon', 'DealUpdated', 'PipelineStageConfigured', 'ActivityLogged', 'TaskChanged', 'LandingPublished', 'LandingUnpublished', 'ContentPublished', 'ContentUnpublished', 'AdCampaignSubmitted', 'AdCampaignApproved', 'AdCampaignPublishRequested', 'AdCampaignReconcileRequested', 'AdCampaignPublished', 'AdCampaignPublishFailed', 'AdCampaignOutcomeUnknown', 'AdCampaignDeliveryChangeRequested', 'AdCampaignDeliveryChanged', 'AdSyncRequested'] as const;
export type DomainEventType = (typeof domainEventTypes)[number];

export const leadCreatedPayload = z.object({
  leadId: z.string(),
  landingVersionId: z.string(),
  formVersionId: z.string(),
  status: z.string(),
});
export const leadAssignedPayload = z.object({ leadId: z.string(), assigneeId: z.string().nullable(), previousAssigneeId: z.string().nullable() });
export const leadQualifiedPayload = z.object({ leadId: z.string(), outcome: z.string(), previousStatus: z.string() });
export const leadProposedPayload = z.object({ leadId: z.string(), previousStatus: z.string() });
export const dealWonPayload = z.object({ leadId: z.string(), dealId: z.string() });
export const dealUpdatedPayload = z.object({ leadId: z.string(), dealId: z.string(), changedFields: z.array(z.string()), previousStageId: z.string(), stageId: z.string() });
export const pipelineStageConfiguredPayload = z.object({ stageId: z.string(), action: z.enum(['created', 'updated']) });
export const activityLoggedPayload = z.object({ activityId: z.string(), targetType: z.string(), targetId: z.string(), type: z.string() });
export const taskChangedPayload = z.object({ taskId: z.string(), targetType: z.string(), targetId: z.string(), action: z.enum(['created', 'updated']) });
export const landingPublishedPayload = z.object({
  landingId: z.string(),
  variantId: z.string(),
  versionId: z.string(),
  versionNumber: z.number().int(),
  /** Public paths whose cached HTML must be refreshed (the page, and its previous path after a rename). */
  paths: z.array(z.string()).default([]),
});
export const landingUnpublishedPayload = z.object({ landingId: z.string(), variantId: z.string(), versionId: z.string().nullable(), paths: z.array(z.string()).default([]) });

export const contentPublishedPayload = z.object({ itemId: z.string(), contentType: z.string(), versionId: z.string(), versionNumber: z.number().int(), paths: z.array(z.string()).default([]) });
export const contentUnpublishedPayload = z.object({ itemId: z.string(), contentType: z.string(), versionId: z.string().nullable(), paths: z.array(z.string()).default([]) });

const adBase = { campaignId: z.string(), revisionId: z.string() };
export const adCampaignSubmittedPayload = z.object({ ...adBase });
export const adCampaignApprovedPayload = z.object({ ...adBase, approvalId: z.string() });
export const adCampaignPublishRequestedPayload = z.object({ ...adBase, operationId: z.string(), landingVersionId: z.string() });
export const adCampaignReconcileRequestedPayload = z.object({ ...adBase, operationId: z.string() });
export const adCampaignPublishedPayload = z.object({ ...adBase, operationId: z.string(), via: z.enum(['publish', 'reconciliation']), /** True when the provider was the development double: consumers must not treat it as real spend. */ demo: z.boolean().default(false) });
export const adCampaignPublishFailedPayload = z.object({ ...adBase, operationId: z.string(), outcome: z.enum(['rejected', 'not-sent']), errorKind: z.string().nullable() });
export const adCampaignOutcomeUnknownPayload = z.object({ ...adBase, operationId: z.string() });

export const adCampaignDeliveryChangeRequestedPayload = z.object({ campaignId: z.string(), changeId: z.string(), action: z.enum(['PAUSE', 'RESUME']) });
export const adCampaignDeliveryChangedPayload = z.object({ campaignId: z.string(), changeId: z.string(), action: z.enum(['PAUSE', 'RESUME']), outcome: z.enum(['applied', 'failed']), deliveryStatus: z.string().nullable(), errorKind: z.string().nullable(), demo: z.boolean().default(false) });
export const adSyncRequestedPayload = z.object({ connectionId: z.string() });

export const eventPayloadSchemas = {
  LeadCreated: leadCreatedPayload,
  LeadAssigned: leadAssignedPayload,
  LeadQualified: leadQualifiedPayload,
  LeadProposed: leadProposedPayload,
  DealWon: dealWonPayload,
  DealUpdated: dealUpdatedPayload,
  PipelineStageConfigured: pipelineStageConfiguredPayload,
  ActivityLogged: activityLoggedPayload,
  TaskChanged: taskChangedPayload,
  LandingPublished: landingPublishedPayload,
  LandingUnpublished: landingUnpublishedPayload,
  ContentPublished: contentPublishedPayload,
  ContentUnpublished: contentUnpublishedPayload,
  AdCampaignSubmitted: adCampaignSubmittedPayload,
  AdCampaignApproved: adCampaignApprovedPayload,
  AdCampaignPublishRequested: adCampaignPublishRequestedPayload,
  AdCampaignReconcileRequested: adCampaignReconcileRequestedPayload,
  AdCampaignPublished: adCampaignPublishedPayload,
  AdCampaignPublishFailed: adCampaignPublishFailedPayload,
  AdCampaignOutcomeUnknown: adCampaignOutcomeUnknownPayload,
  AdCampaignDeliveryChangeRequested: adCampaignDeliveryChangeRequestedPayload,
  AdCampaignDeliveryChanged: adCampaignDeliveryChangedPayload,
  AdSyncRequested: adSyncRequestedPayload,
} as const;
